// import 'bootstrap/dist/css/bootstrap.min.css'
import { SyntheticEvent, createContext, useCallback, useEffect, useRef, useState } from 'react'
import { Row } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import MultiTagSelector from './MultiTagSelector'
import NamesListSection from './components/NamesListSection'
import { fetchLogos, fetchGeneratedWords, fetchTextLogos, textLogoLimit, fetchSingleLogoData } from './lib/api'
import { useSearchParamsMemoized } from './lib/hooks'
import { KeywordCombinations, LogoData, NamesSection, stringToKeywordCombinations } from './lib/types'
import { sanitizeDomain } from './lib/utils'
import DomainNamesList from './components/DomainNamesList'
import { GridLoader } from 'react-spinners'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import LogoContainer from './components/LogoContainer'
import { HiOutlineXMark } from "react-icons/hi2"
import { AxiosError } from 'axios'

export const KeywordsContext = createContext<string[]>([])

function Home() {
  const [inventedNames, setInventedNames] = useState<(KeywordCombinations | null)[]>([])
  const [keywords, setKeywords] = useState<string[]>([])
  const [compoundNames, setCompoundNames] = useState<KeywordCombinations[]>([])
  const [multiwordNames, setMultiwordNames] = useState<(KeywordCombinations | null)[]>([])
  const [synonyms, setSynonyms] = useState<string[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();

  const logosPageLimit = 20

  const [numInventedNames, setNumInventedNames] = useState(logosPageLimit)
  const [numCompoundNames, setNumCompoundNames] = useState(logosPageLimit)
  const [numMultiNames, setNumMultiNames] = useState(logosPageLimit)
  const [numRealWordNames, setNumRealWordNames] = useState(logosPageLimit)
  const [modalShow, setModalShow] = useState(false);

  const [wordLogos, setWordLogos] = useState<LogoData[]>([])
  const { searchParams, memoizedSearchParams } = useSearchParamsMemoized()
  const [selectedWord, setSelectedWord] = useState("")
  const [selectedPage, setSelectedPage] = useState(1)
  const [showLoading, setShowLoading] = useState(false)
  const [textLogoUrls, setTextLogoUrls] = useState<LogoData[]>([])
  const [randomOffset, setRandomOffset] = useState<number>(0)
  const [scrollEndReached, setScrollEndReached] = useState(false)
  const [lastPageReached, setLastPageReached] = useState(false)
  const [selectedTextLogo, setSelectedTextLogo] = useState<LogoData>()
  const [isScrollActive, setScrollActive] = useState(false)

  const isApiCalled = useRef(false);
  const USE_TEXT_LOGOS = false

  const logosPageSize = 9
  const namesLimit = 200

  const clearWords = useCallback(
    () => {
      setInventedNames([])
      setCompoundNames([])
      setMultiwordNames([])
      setSynonyms([])
      clearLimits()
    }, []
  )


  useEffect(() => {
    if (!isApiCalled.current && USE_TEXT_LOGOS) {
      const random = Math.floor(Math.random() * textLogoLimit);
      setRandomOffset(random)
      getTextLogos()
    }
  }, [])


  const onDrawerClose = () => {
    setModalShow(false)
    setLastPageReached(false)
    setScrollActive(false)
  }


  const fetchMoreData = async () => {

    try {
      setShowLoading(true)
      if (keywords.length > 0 && keywords[0]) {
        const logosResp = await fetchLogos(selectedWord, keywords, logosPageSize, selectedPage + 1)
        setShowLoading(false)
        if (logosResp) {
          const { data } = logosResp

          if (data.length === 0) {
            setLastPageReached(true)
          } else {
            setWordLogos([...wordLogos, ...data])
            setSelectedPage(selectedPage + 1)
          }
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.request?.status === 404) {
          const logosResp = await fetchLogos(selectedWord, null, logosPageSize, selectedPage + 1)
          if (logosResp) {
            const { data } = logosResp
            if (data.length === 0) {
              setLastPageReached(true)
            } else {
              setWordLogos([...wordLogos, ...data])
              setSelectedPage(selectedPage + 1)
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    if (scrollEndReached) console.log("end reached");
    console.log({ lastPageReached });

    if (!lastPageReached && isScrollActive) {
      fetchMoreData()
    }

  }, [scrollEndReached])


  const getTextLogos = async () => {
    try {
      const { data } = await fetchTextLogos("company")
      isApiCalled.current = true
      setTextLogoUrls(data)
    } catch (ex) {
      console.log(ex)
    }
  }


  const clearLimits = () => {
    setNumInventedNames(logosPageLimit)
    setNumCompoundNames(logosPageLimit)
    setNumMultiNames(logosPageLimit)
    setNumRealWordNames(logosPageLimit)
    setScrollActive(false)
  }

  const generateNames = (tags: string[]) => {
    setSearchParams({ keywords: tags.join(',') })
    clearLimits()
  }

  useEffect(() => {

    const keywordsList: string | null = searchParams.get('keywords')
    if (keywordsList != null && keywordsList.length > 0) {
      const keywords = keywordsList?.split(',').filter(keyword => keyword !== "") || []
      setKeywords(keywords)
    } else {
      clearWords()
    }
    const random = Math.floor(Math.random() * textLogoLimit);
    setRandomOffset(random)
  }, [memoizedSearchParams])


  const showSeeMore = (sectionName: NamesSection) => {
    let result = false
    switch (sectionName) {
      case NamesSection.InventedNames:
        result = numInventedNames < namesLimit && numInventedNames < inventedNames.length
        break;

      case NamesSection.CompoundNames:
        result = numCompoundNames < namesLimit && numCompoundNames < compoundNames.length
        break;

      case NamesSection.MultiwordNames:
        result = numMultiNames < namesLimit && numMultiNames < multiwordNames.length
        break;

      case NamesSection.RealwordNames:
        result = numRealWordNames < namesLimit && numRealWordNames < synonyms.length
        break;

      default:
        break;
    }

    return result
  }


  const getGeneratedWords = async (keywords: string[]) => {
    const { inventedNames, compoundNames, multiWordNames, synonyms } = await fetchGeneratedWords(keywords)
    setInventedNames(inventedNames)
    setCompoundNames(compoundNames)
    setMultiwordNames(multiWordNames)
    setSynonyms(synonyms)
  }

  useEffect(() => {
    console.log(keywords);

    if (keywords.length === 0) {
      clearWords()
    } else {
      getGeneratedWords(keywords)
    }
  }, [keywords])

  const onSeeMoreClick = (sectionName: NamesSection) => {
    let limit = namesLimit;
    switch (sectionName) {
      case NamesSection.InventedNames:
        setNumInventedNames(Math.min(numInventedNames + 16, limit));
        break;

      case NamesSection.CompoundNames:
        setNumCompoundNames(Math.min(numCompoundNames + 16, limit));
        break;

      case NamesSection.MultiwordNames:
        setNumMultiNames(Math.min(numMultiNames + 16, limit));
        break;

      case NamesSection.RealwordNames:
        setNumRealWordNames(Math.min(numRealWordNames + 16, limit));
        break;

      default:
        break;
    }
  }

  const handleNameItemClick = async (word: string, keywords: string[], selectedTextLogo: LogoData | undefined) => {
    const page = 1
    setSelectedWord(word)
    if (USE_TEXT_LOGOS && selectedTextLogo) {
      const singleLogoData = await fetchSingleLogoData(selectedTextLogo.id, word)
      setSelectedTextLogo(singleLogoData)
      setLogoListPage(page, word)
    } else if (!USE_TEXT_LOGOS) {
      setLogoListPage(page, word)
    }
  }

  const setLogoListPage = async (numPage: number, word: string) => {
    setModalShow(true)
    setWordLogos([])
    try {
      setShowLoading(true)
      if (keywords.length > 0 && keywords[0]) {
        const logosResp = await fetchLogos(word, keywords, logosPageSize, numPage)

        setShowLoading(false)
        if (logosResp) {
          const { data } = logosResp
          setWordLogos(data)
          setSelectedPage(numPage)
        }
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.request?.status === 404) {
          const logosResp = await fetchLogos(word, null, logosPageSize, numPage)
          if (logosResp) {
            const { data } = logosResp
            setWordLogos(data)
            setSelectedPage(numPage)
          }
        }
      }
    }
    setScrollActive(true)
  }

  const onLogoItemClick = (url: string) => {
    window.open(url)
  }

  const handleScroll = (event: SyntheticEvent) => {
    const containerHeight = event.currentTarget.clientHeight;
    const scrollHeight = event.currentTarget.scrollHeight;

    const scrollTop = event.currentTarget.scrollTop;
    const scrollPosition = ((scrollTop + containerHeight) / scrollHeight) * 100;
    if (scrollPosition > 85) {
      setScrollEndReached(true)
    } else {
      setScrollEndReached(false)
    }
  }

  return (

    <KeywordsContext.Provider value={keywords}>
      <div>

        <main className="main">

          <div className='navbar navbar-expand-lg mb-4'>
            <MultiTagSelector onGenerateClick={(e, tags) => generateNames(tags)} keywords={keywords} />
          </div>
          <div className={"bodyContainer"}>
            {inventedNames.length > 0 &&
              <NamesListSection
                textLogoUrls={textLogoUrls}
                useTextLogos={USE_TEXT_LOGOS}
                randomOffset={randomOffset}
                namesList={inventedNames.slice(0, numInventedNames)}
                title={"Invented names"}
                sectionType={NamesSection.InventedNames}
                showSeeMore={showSeeMore}
                onNameItemClick={handleNameItemClick}
                onSeeMoreClick={onSeeMoreClick} />
            }
            {compoundNames.length > 0 &&
              <NamesListSection
                textLogoUrls={textLogoUrls}
                useTextLogos={USE_TEXT_LOGOS}
                randomOffset={randomOffset}
                namesList={compoundNames.slice(0, numCompoundNames)}
                title={"Compound names"}
                sectionType={NamesSection.CompoundNames}
                showSeeMore={showSeeMore}
                onNameItemClick={handleNameItemClick}
                onSeeMoreClick={onSeeMoreClick} />
            }
            {multiwordNames.length > 0 &&
              <NamesListSection
                textLogoUrls={textLogoUrls}
                useTextLogos={USE_TEXT_LOGOS}
                randomOffset={randomOffset}
                namesList={multiwordNames.slice(0, numMultiNames)}
                title={"Multiword names"}
                sectionType={NamesSection.MultiwordNames}
                showSeeMore={showSeeMore}
                onNameItemClick={handleNameItemClick}
                onSeeMoreClick={onSeeMoreClick} />
            }
            {synonyms.length > 0 &&
              <NamesListSection
                textLogoUrls={textLogoUrls}
                useTextLogos={USE_TEXT_LOGOS}
                randomOffset={randomOffset}
                namesList={synonyms.slice(0, numRealWordNames).map((item) => stringToKeywordCombinations(item, keywords))}
                title={"Real-word names"}
                sectionType={NamesSection.RealwordNames}
                showSeeMore={showSeeMore}
                onNameItemClick={handleNameItemClick}
                onSeeMoreClick={onSeeMoreClick} />
            }

          </div>
          <Drawer
            open={modalShow}
            onClose={onDrawerClose}
            direction="right"
            lockBackgroundScroll={true}
            className="bngDrawer"
          >
            <div className='drawerBody' onScroll={handleScroll}>
              <div>
                <div className='drawerHeader pb-0'>
                  <span className="panelHeading ps-0 floatLeft">Domains</span>
                  <span className='close-btn' onClick={() => setModalShow(false)}>
                    <HiOutlineXMark />
                  </span>
                </div>
                <DomainNamesList
                  domainName={sanitizeDomain(selectedWord)}
                ></DomainNamesList>
                <div className='text-center'>
                  <span className="panelHeading logoHeader">
                    Logo Ideas
                  </span>
                </div>
                <div className='logosListWrapper container'>
                  <Row className='py-2'>


                    {selectedTextLogo &&
                      <LogoContainer
                        showTwoColumns="True"
                        key={-1}
                        isLoaded={true}
                        onClick={() => onLogoItemClick(selectedTextLogo.studio_url)}

                        src={selectedTextLogo.img_url}
                        onLoad={() => { }}>

                      </LogoContainer>
                    }

                    {wordLogos.length > 0 && wordLogos.map((item, index) => {
                      return (
                        <LogoContainer
                          showTwoColumns="True"
                          key={index}
                          isLoaded={true}
                          onClick={() => onLogoItemClick(item.studio_url)}

                          src={item.img_url}
                          onLoad={() => { }}
                        >

                        </LogoContainer>
                      )
                    })}

                  </Row>
                  {showLoading &&
                    (<div className='spinnerWrapper'><GridLoader color="#646bd" /></div>)
                  }
                  {!showLoading && wordLogos.length === 0 &&
                    (
                      <div className='p-2 d-flex justify-content-center rounded fw-medium border text-black-50 m-2'>No logos Found</div>
                    )
                  }
                </div>
              </div>
            </div>
          </Drawer>
        </main>
      </div>
    </KeywordsContext.Provider>
  )
}

export default Home;
